import { Code, ConnectError } from '@bufbuild/connect';
import { useRef } from 'react';

import api from '@/shared/api/api';
import { useAppDispatch } from '@/shared/hooks';
import { debug, isConnectNetworkError } from '@/shared/lib';

import { accessModel } from '@/entities/access';

import { SharedByMeFilterSettingsType } from '../config/types';

export type UseSharedByMeStreamArgs = {
  organizationID: string;
  searchValue?: string;
};

export const useSharedByMeStream = ({
  organizationID,
  searchValue = '',
}: UseSharedByMeStreamArgs) => {
  // use to restore patients state for infinity scroll
  // must be reset after any filters update
  const abortControllerRef = useRef<AbortController | null>(null);

  const dispatch = useAppDispatch();

  const openSharedByMeStream = async (
    filterSettings: SharedByMeFilterSettingsType = {
      searchString: searchValue,
      id: '',
    },
  ) => {
    if (abortControllerRef.current) {
      abortControllerRef.current.abort();
    }

    abortControllerRef.current = new AbortController();

    const { searchString, id, sortBy } = filterSettings;

    dispatch(accessModel.sharingActions.setLoading('pending'));

    try {
      const sharedByMeStream = api.access.sharedByMeStream(
        {
          OrganizationID: organizationID,

          SearchString: searchString,
          Sort: sortBy,
          StartFromSharingID: id,
          Limit: 20,
        },
        { signal: abortControllerRef.current.signal },
      );

      for await (const { Update } of sharedByMeStream) {
        switch (Update.case) {
          case 'InitialSharesList': {
            dispatch(accessModel.sharingActions.setMany(Update.value.Shares));

            dispatch(accessModel.sharingActions.setLoading('succeeded'));
            break;
          }
          case 'TotalOrganizationPatientCount': {
            dispatch(
              accessModel.actions.setTotalOrganizationPatientCount(
                Update.value,
              ),
            );
            break;
          }
          case 'SharedByMeCount': {
            dispatch(accessModel.actions.setSharedByMeCount(Update.value));
            break;
          }
          case 'SharedWithMeCount': {
            dispatch(accessModel.actions.setSharedWithMeCount(Update.value));
            break;
          }
          case 'UpdatedSharing': {
            dispatch(accessModel.sharingActions.setNewestOne(Update.value));
            break;
          }
        }
      }
    } catch (error) {
      const connectError = ConnectError.from(error);

      if (isConnectNetworkError(connectError)) {
        debug('Network error detected, attempting to restart stream...');
        closeSharedByMeStream(false);
        openSharedByMeStream();
      }

      if (error instanceof ConnectError && error.code !== Code.Canceled) {
        dispatch(accessModel.sharingActions.setLoading('failed'));
      }
    }
  };

  const closeSharedByMeStream = (resetStore = true) => {
    if (abortControllerRef.current) {
      abortControllerRef.current.abort();
      abortControllerRef.current = null;
    }

    if (resetStore) {
      dispatch(accessModel.sharingActions.removeAll());
    }
  };

  return { openSharedByMeStream, closeSharedByMeStream };
};
